import type { IComponentController, IScope } from "angular";

import type { IProfileService } from "App/Modules/Auth";
import type { IHoliday, ISchedule, IScheduleException, IScheduleTimePeriod } from "App/Modules/Models";
import { DayType, ScheduleExceptionFunctions, ScheduleTypes } from "App/Modules/Models";

import { cloneDeep, find, first, get, head, isEmpty, isEqual, isNil, last, takeRight } from "lodash";
import type { Moment } from "moment-timezone";
import { utc, weekdaysMin } from "moment-timezone";

const scheduleColors = {
    holidaySchedule: {
        colors: ["#E0C5FF"],
    },
    scheduleExceptions: {
        colors: ["#FFBCBC", "#EC6065"],
    },
    temporaryWeeklySchedule: {
        colors: ["#FBC976"],
    },
};

export default class CalendarViewComponentController implements IComponentController {
    public static $inject: string[] = ["$scope", "mmProfileService"];

    public extraClasses: string;

    public timeZone: string;

    public readonly: boolean;

    public weekdays: string[];

    public startOfMonth: Moment;

    public previousInitialDate: Moment;

    public initialDate: Moment;

    public schedule: ISchedule;

    public previousSchedule: ISchedule;

    public calendar: Array<{
        days: Array<{
            moment: Moment;
            schedule: Array<{
                color: string;
                runningException?: IScheduleException;
                timePeriods?: IScheduleTimePeriod[];
                type: ScheduleTypes;
            }>;
        }>;
    }>;

    constructor(private readonly $scope: IScope, private readonly profileService: IProfileService) {}

    public $onInit() {
        if (!this.schedule) {
            return;
        }

        ScheduleExceptionFunctions.enhanceScheduleExceptions(this.schedule.scheduleExceptions, this.timeZone);

        this.previousSchedule = cloneDeep(this.schedule);
        this.previousInitialDate = this.initialDate.clone();
        this.weekdays = weekdaysMin(true);

        this.initCalendarForMonth();
    }

    public $doCheck() {
        if (
            (this.schedule && this.previousSchedule && !isEqual(this.schedule, this.previousSchedule)) ||
            (this.initialDate && this.previousInitialDate && !this.initialDate.isSame(this.previousInitialDate))
        ) {
            this.initCalendarForMonth();
            this.previousSchedule = cloneDeep(this.schedule);
            this.previousInitialDate = this.initialDate.clone();
        }
    }

    public initCalendarForMonth() {
        this.startOfMonth = this.initialDate
            .clone()
            .add(this.readonly ? 1 : 0, "month")
            .startOf("month");
        this.calendar = this.createCalendar();
    }

    // Source: https://www.iditect.com/how-to/53707293.html
    private calcNumberWeeksInMonth(date: Moment) {
        const dateFirst = date.clone().date(1);
        const dateLast = date.clone().date(date.daysInMonth());
        const startWeek = dateFirst.isoWeek();
        const endWeek = dateLast.isoWeek();

        if (endWeek < startWeek) {
            // Yearly overlaps, month is either DEC or JAN
            if (dateFirst.month() === 0) {
                // January
                return endWeek + 1;
            }
            // December
            if (dateLast.isoWeekday() === 7) {
                // Sunday is last day of year
                return endWeek - startWeek + 1;
            }
            // Sunday is NOT last day of year
            return dateFirst.isoWeeksInYear() - startWeek + 1;
        }
        return endWeek - startWeek + 1;
    }

    public createCalendar() {
        const calendar = [];
        const firstWeek = this.startOfMonth.week();
        const numberOfWeeksInMonth = this.calcNumberWeeksInMonth(this.startOfMonth);
        const lastWeek = firstWeek + numberOfWeeksInMonth;

        for (let week = firstWeek; week < lastWeek; week++) {
            calendar.push({
                days: new Array(7).fill(null).map((val, i) => {
                    const date: Moment = this.startOfMonth.clone().week(week).startOf("week").add(i, "day");
                    return {
                        moment: date,
                        schedule: [
                            this.getTemporaryScheduleConfig(date),
                            this.getHolidayScheduleConfig(date),
                            this.getScheduleExceptionConfig(date),
                        ],
                    };
                }),
                week,
            });
        }

        return calendar;
    }

    public getTemporaryScheduleConfig(date: Moment) {
        if (
            !this.schedule ||
            !this.schedule.temporaryWeeklySchedule ||
            !this.schedule.temporaryWeeklySchedule.datePeriods
        ) {
            return undefined;
        }

        const scheduleForDate = this.schedule.temporaryWeeklySchedule.datePeriods.find((schedule) =>
            date.isBetween(schedule.dateStart, schedule.dateStop, "day", "[]"),
        );

        if (!scheduleForDate || !scheduleForDate.dateStart || !scheduleForDate.dateStop) {
            return undefined;
        }

        const timePeriods =
            this.schedule.temporaryWeeklySchedule.daySchedules[date.isoWeekday() - 1].scheduleTimePeriods;

        return {
            color: !isEmpty(timePeriods) ? scheduleColors.temporaryWeeklySchedule.colors[0] : null,
            timePeriods: timePeriods || [],
            type: ScheduleTypes.TemporarySchedule,
        };
    }

    public getHolidayScheduleConfig(date: Moment) {
        if (!this.schedule || !this.schedule.holidaySchedule || !this.schedule.holidayConfigurations) {
            return undefined;
        }

        const scheduleForDate: IHoliday = this.schedule.holidaySchedule.holidays.find((holiday) =>
            date.isBetween(
                holiday.dtStart.clone().subtract(1, "day"),
                holiday.dtStop.clone().add(1, "day"),
                "day",
                "[]",
            ),
        );

        if (!scheduleForDate) {
            return undefined;
        }

        const { daySchedules } = head(this.schedule.holidayConfigurations);
        let timePeriods;

        const startOfDate = date.clone().startOf("day");
        if (startOfDate.isBetween(scheduleForDate.dtStart, scheduleForDate.dtStop, null, "[]")) {
            const dayOnHoliday = find(daySchedules, (schedule) => schedule.dayType === DayType.DayOnHoliday);
            timePeriods = dayOnHoliday.scheduleTimePeriods;
        } else if (
            startOfDate.isBetween(
                scheduleForDate.dtStart.clone().subtract(1, "day"),
                scheduleForDate.dtStart,
                null,
                "[)",
            )
        ) {
            const dayBeforeHoliday = find(daySchedules, (schedule) => schedule.dayType === DayType.DayBeforeHoliday);
            timePeriods = dayBeforeHoliday.scheduleTimePeriods;
        } else if (
            startOfDate.isBetween(scheduleForDate.dtStop, scheduleForDate.dtStop.clone().add(1, "day"), null, "[]")
        ) {
            const dayAfterHoliday = find(daySchedules, (schedule) => schedule.dayType === DayType.DayAfterHoliday);
            timePeriods = dayAfterHoliday.scheduleTimePeriods;
        }

        return {
            color: !isEmpty(timePeriods) ? scheduleColors.holidaySchedule.colors[0] : null,
            timePeriods,
            type: ScheduleTypes.HolidaySchedule,
        };
    }

    public getScheduleExceptionConfig(date: Moment) {
        if (!this.schedule || !this.schedule.scheduleExceptions) {
            return undefined;
        }

        const scheduleForDate: IScheduleException = this.schedule.scheduleExceptions.find((schedule) => {
            if (!schedule.dtStart || !schedule.dtStop) {
                return undefined;
            }

            return date
                .clone()
                .tz(this.timeZone)
                .isBetween(schedule.dtStart, schedule.dtStop.clone().subtract(1, "second"), "day", "[]");
        });

        if (scheduleForDate && scheduleForDate.dtStart && scheduleForDate.dtStop) {
            return {
                color: scheduleForDate.isOnType
                    ? scheduleColors.scheduleExceptions.colors[1]
                    : scheduleColors.scheduleExceptions.colors[0],
                runningException: scheduleForDate,
                type: ScheduleTypes.ExceptionSchedule,
            };
        }
        return undefined;
    }

    public isToday(date: Moment): boolean {
        return utc().tz(this.timeZone).isSame(date, "day");
    }

    public isNotInMonth(date: Moment): boolean {
        return (
            date.isBefore(this.startOfMonth) ||
            date.isBefore(utc().tz(this.timeZone).startOf("day")) ||
            date.isAfter(this.startOfMonth.clone().endOf("month"))
        );
    }

    public shouldShowGradient(calculatedDay: Moment, exceptionSchedule: IScheduleException) {
        const moment: Moment = calculatedDay.clone().tz(this.timeZone);
        const overlapsStartDate =
            exceptionSchedule &&
            moment.isSame(exceptionSchedule.dtStart, "day") &&
            exceptionSchedule.dtStart.diff(moment.startOf("day"), "minutes") >= 1;
        const overlapsEndDate =
            exceptionSchedule &&
            moment.isSame(exceptionSchedule.dtStop, "day") &&
            moment.endOf("day").diff(exceptionSchedule.dtStop, "minutes") > 1;

        return overlapsStartDate || overlapsEndDate;
    }

    public getStyle(day: any): any {
        const style: any = {
            background: get(first(day.schedule), "color"),
        };

        const filteredSchedule = day.schedule.filter((item: any) => !isNil(item) && !isNil(item.color));

        const exceptionSchedule = filteredSchedule.find((item: any) => item.type === ScheduleTypes.ExceptionSchedule);

        const showGradient =
            exceptionSchedule && this.shouldShowGradient(day.moment, exceptionSchedule.runningException);

        if (filteredSchedule.length > 0 && !showGradient) {
            style.background = get(last(filteredSchedule), "color");
        } else if (filteredSchedule.length > 0 && showGradient) {
            const gradientColors: string[] = takeRight(
                ["transparent", ...filteredSchedule.map((input: any) => input.color)],
                2,
            );

            if (day.moment.clone().tz(this.timeZone).isSame(exceptionSchedule.runningException.dtStop, "day")) {
                gradientColors.reverse();
            }

            style.background = `linear-gradient(to right bottom, ${gradientColors[0]} 49%, ${gradientColors[1]} 50%) no-repeat`;
            style["background-size"] = "102% 102%";
        }

        return style;
    }

    public nextMonth() {
        this.initialDate.add(1, "month").startOf("month");
    }

    public previousMonth() {
        this.initialDate.subtract(1, "month").startOf("month");
    }

    public isMondayFirstDayOfWeek() {
        return utc().startOf("week").isoWeekday() === DayType.Monday;
    }

    public isPreviousMonthButtonDisabled() {
        return this.initialDate.isSame(utc().tz(this.timeZone), "month");
    }

    public isNextMonthButtonDisabled() {
        return this.initialDate.isSame(utc().tz(this.timeZone).clone().add(1, "year"), "month");
    }

    public isReadonlyCalendarHidden() {
        return this.readonly && this.initialDate.isSame(utc().tz(this.timeZone).clone().add(1, "year"), "month");
    }
}
